import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Upload,
  Modal,
  Form,
  Input,
  Avatar,
  Tag,
  DatePicker,
  InputNumber,
  message,
} from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import Main from "../components/layout/Main";
import {
  ToTopOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const CanellactionReasonManager = () => {
  const { request } = useRequest();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [selected, setSelected] = useState(null);
  const [visible, setVisible] = useState(false);
  const { confirm } = Modal;

  const showConfirm = (record) => {
    confirm({
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to change the status?",
      onOk() {
        statusChange(record);
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const handleDelete = (id) => {
    confirm({
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to delete this reason?",
      onOk() {
        setLoading(true);
        request({
          url: `/admin/cancel-reason/delete/${id}`,
          method: "DELETE",
          onSuccess: (data) => {
            setLoading(false);
            if (data.status) {
              message.success(" Reason deleted successfully");
              setRefresh((prev) => !prev);
            } else {
              message.error("Failed to delete cancel reason");
            }
          },
          onError: (error) => {
            console.error("Error deleting cancel reason:", error);
            ShowToast(error, Severty.ERROR);
            setLoading(false);
          },
        });
      },
      onCancel() {
        console.log("Delete canceled");
      },
    });
  };


  const statusChange = (record) => {
    setLoading(true);
    request({
      url: `/admin/cancel-reason/status-change/${record}`,
      method: "PUT",
      onSuccess: (data) => {
        setRefresh((prev) => !prev);
        setLoading(false);
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Status",
      filters: [
        {
          text: "Active",
          value: true,
        },
        {
          text: "Inactive",
          value: false,
        },
      ],
      onFilter: (value, record) => record.is_status === value,
      render: (_, { is_status, _id }) => {
        let color = is_status ? "green" : "red";
        return (
          <Tag onClick={() => showConfirm(_id)} color={color} key={is_status}>
            {is_status ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Created date",
      key: "created_at",
      dataIndex: "created_at",
      render: (_, { created_at }) => {
        return new Date(created_at).toLocaleDateString();
      },
    },
    {
      title: "Action",
      render: (_, record) => {
        return (
          <div className="cta_wrap">
            <Button
              onClick={() => {
                setSelected(record);
                setVisible(true);
              }}
              style={{ marginRight: 8 }}
            >
              <i className="fa fa-light fa-pen"></i>
              Edit
            </Button>
            <Button
              className="tb-view"
              onClick={() => handleDelete(record._id)}
            >
              <i className="fa fa-light fa-trash"></i>
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    request({
      url: "/admin/cancel-reason/list",
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs);
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
        setLoading(false);
      },
    });
  }, [refresh]);

  return (
    <Main>
      <div className="tabled">
        <Row>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Cancel Reason Management"
              extra={
                <Button onClick={() => setVisible(true)}>Add Reason</Button>
              }
            >
              <div className="table-responsive">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  pagination={true}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      {visible && (
        <AddForm
          show={visible}
          hide={() => {
            setSelected(null);
            setVisible(false);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}
    </Main>
  );
};

const AddForm = ({ show, hide, data, refresh }) => {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        title: data.title,
      });
    } else {
      form.resetFields();
    }
  }, [data, form]);

  const handleSubmit = (values) => {
    setLoading(true);

    const payload = {
      title: values.title,
    };

    const url = data
      ? `/admin/cancel-reason/edit/${data._id}`
      : "/admin/cancel-reason/add";
    const method = data ? "PUT" : "POST";

    request({
      url,
      method,
      data: payload,
      onSuccess: (response) => {
        setLoading(false);
        if (response.status) {
          ShowToast(response.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(response.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(
          error.response?.data?.message || "Something went wrong",
          Severty.ERROR
        );
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      open={show}
      title={data ? "Update Reason" : "Create a new Reason"}
      okText="Ok"
      onCancel={hide}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
    >
      <Form id="create" form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please input the title" }]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CanellactionReasonManager;
